import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiEdit2, FiTrash2, FiClock, FiEye, FiLock } from "react-icons/fi";
import { MdDevices, MdAddCircleOutline } from "react-icons/md";
import Card from "../../../../components/card";
import api from "../../../../services/axios";
import { Equipament, statusLabels, EquipamentStatus } from "../../../../interfaces/Equipament";
import ModalComputerDetail from "./ModalComputerDetail";
import ModalHistorico from "./ModalHistorico";
import ModalAddEditComputer from "./ModalAddEditComputer";
import ModalDeleteComputer from "./ModalDeleteComputer";
import {
  fleetLatest,
  lastSeenLabel,
  widgetStatus,
  WIDGET_STATUS_META,
} from "../widgetVersion";

interface ClientOption {
  id: number;
  legalName: string;
  tradeName: string;
}

interface EquipamentTableProps {
  data: Equipament[];
  onRefresh: () => void;
}

const fmtBRL = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const statusBadge: Record<EquipamentStatus, string> = {
  em_uso: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  disponivel: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  danificado: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const selectCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white sm:w-auto";

const EquipamentTable: React.FC<EquipamentTableProps> = ({ data, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortedColumn, setSortedColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  // "Sem dono identificado" é o estado que a frota sem login inevitavelmente produz: máquina fora
  // do domínio, ou pessoa que o cadastro do NINA ainda não conhece. Só vira trabalho resolvível se
  // der para LISTAR — senão fica um punhado de linhas incompletas perdidas no meio das outras.
  const [ownerFilter, setOwnerFilter] = useState<string>("all");

  // Modals
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openHistoricoModal, setOpenHistoricoModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedComputer, setSelectedComputer] = useState<Equipament | null>(null);

  const [clients, setClients] = useState<ClientOption[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get<ClientOption[]>("/Client/Clients");
        setClients((res.data ?? []).filter((c: any) => c.flActive));
      } catch {
        // silent
      }
    };
    load();
  }, []);

  const clientNameById = useMemo(() => {
    const map = new Map<number, string>();
    clients.forEach((c) => map.set(c.id, c.tradeName));
    return map;
  }, [clients]);

  // A régua de "atualizado" é a maior versão que ALGUMA máquina reportou — não uma constante que
  // alguém teria de lembrar de bumpar a cada release (ver widgetVersion.ts).
  const fleetVersion = useMemo(() => fleetLatest(data.map((e) => e.widgetVersion)), [data]);

  const handleSort = (columnKey: string) => {
    if (sortedColumn === columnKey) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortedColumn(columnKey);
      setSortOrder("asc");
    }
  };

  const filteredData = data.filter((computer) => {
    const matchesSearch =
      computer.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      computer.serieNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (computer.actualUser?.toLowerCase().includes(searchTerm.toLowerCase()) || false);

    const matchesStatus = statusFilter === "all" || computer.status === statusFilter;

    const matchesOwner =
      ownerFilter === "all" ||
      (ownerFilter === "unidentified" && !computer.employeeId) ||
      (ownerFilter === "pinned" && !!computer.ownerPinned);

    return matchesSearch && matchesStatus && matchesOwner;
  });

  const sortedData = sortedColumn
    ? [...filteredData].sort((a, b) => {
        const aValue = a[sortedColumn as keyof Equipament];
        const bValue = b[sortedColumn as keyof Equipament];
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        return sortOrder === "asc"
          ? aValue > bValue ? 1 : -1
          : aValue < bValue ? 1 : -1;
      })
    : filteredData;

  const handleViewDetail = (computer: Equipament) => {
    setSelectedComputer(computer);
    setOpenDetailModal(true);
  };

  const handleViewHistorico = (computer: Equipament) => {
    setSelectedComputer(computer);
    setOpenHistoricoModal(true);
  };

  const handleEdit = (computer: Equipament) => {
    setSelectedComputer(computer);
    setOpenEditModal(true);
  };

  const handleDelete = (computer: Equipament) => {
    setSelectedComputer(computer);
    setOpenDeleteModal(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const columns: { key: string; label: string; align?: "right" }[] = [
    { key: "serieNumber", label: "Nº Série" },
    { key: "model", label: "Modelo" },
    { key: "year", label: "Ano" },
    { key: "processor", label: "Processador" },
    { key: "memory", label: "Memória" },
    { key: "storage", label: "HD" },
    { key: "status", label: "Status" },
    { key: "actualUser", label: "Usuário Atual" },
    { key: "widgetVersion", label: "Widget" },
    { key: "monthlyValue", label: "Valor", align: "right" },
    { key: "payerClientId", label: "Pagador" },
    { key: "purchaseDate", label: "Data Compra" },
  ];

  return (
    <>
      <Card extra="p-5">
        {/* Toolbar */}
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
            {/* Status Filter */}
            <div className="flex flex-col gap-1.5 sm:flex-1">
              <label htmlFor="statusFilter" className="sr-only">
                Filtrar por status
              </label>
              <select
                id="statusFilter"
                className={selectCls}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos os Status</option>
                <option value="em_uso">Em Uso</option>
                <option value="disponivel">Disponíveis</option>
                <option value="danificado">Danificados</option>
              </select>
            </div>

            {/* Dono */}
            <div className="flex flex-col gap-1.5 sm:flex-1">
              <label htmlFor="ownerFilter" className="sr-only">
                Filtrar por dono
              </label>
              <select
                id="ownerFilter"
                className={selectCls}
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
              >
                <option value="all">Qualquer dono</option>
                <option value="unidentified">Sem dono identificado</option>
                <option value="pinned">Dono fixado pela TI</option>
              </select>
            </div>

            {/* Search */}
            <div className="relative sm:w-64">
              <label htmlFor="equipSearch" className="sr-only">
                Buscar equipamento
              </label>
              <FiSearch
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                id="equipSearch"
                type="text"
                placeholder="Buscar por modelo, série ou usuário…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </div>
          </div>

          {/* Add Button */}
          <button
            type="button"
            onClick={() => {
              setSelectedComputer(null);
              setOpenEditModal(true);
            }}
            className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98]"
          >
            <MdAddCircleOutline className="h-4 w-4" aria-hidden="true" />
            Novo Computador
          </button>
        </div>

        {/* Table */}
        <div className="mt-5 overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-[0_8px_30px_rgba(112,144,176,0.12)] dark:border-white/10 dark:bg-navy-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-900">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={
                      sortedColumn === col.key
                        ? sortOrder === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    className={`whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 ${
                      col.align === "right" ? "text-right" : "text-left"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-navy-700 dark:hover:text-white ${
                        col.align === "right" ? "flex-row-reverse" : ""
                      }`}
                    >
                      {col.label}
                      {sortedColumn === col.key && (
                        <span aria-hidden="true">{sortOrder === "asc" ? "↑" : "↓"}</span>
                      )}
                    </button>
                  </th>
                ))}
                <th
                  scope="col"
                  className="whitespace-nowrap px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300"
                >
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-12 text-center text-sm text-gray-700 dark:text-gray-300"
                  >
                    <MdDevices
                      className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-gray-600"
                      aria-hidden="true"
                    />
                    Nenhum equipamento encontrado
                  </td>
                </tr>
              ) : (
                sortedData.map((computer) => (
                  <tr
                    key={computer.id}
                    className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-navy-700 dark:text-white">
                      {computer.serieNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="block max-w-[200px] truncate" title={computer.model}>
                        {computer.model}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">
                      {computer.year}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="block max-w-[180px] truncate" title={computer.processor}>
                        {computer.processor}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {computer.memory}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {computer.storage}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusBadge[computer.status]}`}
                      >
                        {statusLabels[computer.status]}
                      </span>
                    </td>
                    {/* Dono. Nome sem vínculo com o cadastro não é a mesma coisa que dono
                        identificado — e a diferença é o que separa "resolvido" de "ninguém reparou". */}
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="flex items-center gap-1.5">
                        {computer.actualUser || "—"}
                        {computer.ownerPinned && (
                          <span
                            title="Dono fixado pela TI: nenhuma automação troca"
                            className="text-gray-400 dark:text-gray-500"
                            aria-label="Dono fixado pela TI"
                          >
                            <FiLock className="h-3.5 w-3.5" aria-hidden="true" />
                          </span>
                        )}
                        {!computer.employeeId && computer.actualUser && (
                          <span
                            title="Nome sem vínculo com o cadastro de colaboradores"
                            className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                          >
                            não identificado
                          </span>
                        )}
                      </span>
                    </td>
                    {/* Widget: versão + quando deu sinal. A régua é a maior versão da frota. */}
                    <td className="whitespace-nowrap px-4 py-3">
                      {(() => {
                        const status = widgetStatus(
                          computer.widgetVersion,
                          computer.lastSeenAt,
                          fleetVersion,
                          computer.widgetUpdateStatus
                        );
                        const meta = WIDGET_STATUS_META[status];
                        return (
                          <div className="flex flex-col gap-1">
                            <span className="text-sm tabular-nums text-navy-700 dark:text-white">
                              {computer.widgetVersion || "—"}
                            </span>
                            <span
                              className={`inline-flex w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.chip}`}
                              title={
                                computer.widgetUpdateError
                                  ? `Updater: ${computer.widgetUpdateError}`
                                  : computer.lastSeenAt
                                  ? `Último contato: ${new Date(computer.lastSeenAt).toLocaleString("pt-BR")}`
                                  : "Esta máquina nunca reportou"
                              }
                            >
                              {status === "atualizado"
                                ? lastSeenLabel(computer.lastSeenAt)
                                : meta.label}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">
                      {computer.monthlyValue != null ? fmtBRL(computer.monthlyValue) : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="block max-w-[160px] truncate">
                        {computer.payerClientId
                          ? clientNameById.get(computer.payerClientId) ?? `#${computer.payerClientId}`
                          : "—"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">
                      {formatDate(computer.purchaseDate)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleViewDetail(computer)}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-dv-blue-50 hover:text-dv-blue-600 dark:text-gray-300 dark:hover:bg-navy-600"
                          aria-label={`Ver detalhes de ${computer.model}`}
                          title="Ver Detalhes"
                        >
                          <FiEye className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleViewHistorico(computer)}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-dv-cyan-50 hover:text-dv-cyan-600 dark:text-gray-300 dark:hover:bg-navy-600"
                          aria-label={`Ver histórico de ${computer.model}`}
                          title="Ver Histórico"
                        >
                          <FiClock className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(computer)}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600 dark:text-gray-300 dark:hover:bg-navy-600"
                          aria-label={`Editar ${computer.model}`}
                          title="Editar"
                        >
                          <FiEdit2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(computer)}
                          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-gray-300 dark:hover:bg-navy-600"
                          aria-label={`Excluir ${computer.model}`}
                          title="Excluir"
                        >
                          <FiTrash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modals */}
      <ModalComputerDetail
        open={openDetailModal}
        setOpen={setOpenDetailModal}
        computer={selectedComputer}
        onChanged={onRefresh}
      />

      <ModalHistorico
        open={openHistoricoModal}
        setOpen={setOpenHistoricoModal}
        computer={selectedComputer}
      />

      <ModalAddEditComputer
        open={openEditModal}
        setOpen={setOpenEditModal}
        computer={selectedComputer}
        onSave={onRefresh}
      />

      <ModalDeleteComputer
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        computer={selectedComputer}
        onDelete={onRefresh}
      />
    </>
  );
};

export default EquipamentTable;
